import React, { useEffect, useState } from "react";
import { PoundSterling, Plus, CheckCircle2, FileDown, ShieldCheck, ClipboardList, FileSignature } from "lucide-react";
import api, { apiError, fileUrl, openPdf } from "@/lib/api";
import { fmtDate, terms } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const gbp = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ContractorPicker({ onPick }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/contractors").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (!list.length) return null;
  return (
    <div>
      <Label className="overline">Pick saved contractor</Label>
      <Select onValueChange={(id) => { const c = list.find((x) => x.id === id); if (c) onPick(c); }}>
        <SelectTrigger data-testid="contractor-picker" className="mt-1.5"><SelectValue placeholder="From directory…" /></SelectTrigger>
        <SelectContent>{list.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.trade ? ` — ${c.trade}` : ""}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export function IssueExtras({ issue, role, reload }) {
  const staff = ["admin", "operations", "compliance"].includes(role);
  const [cost, setCost] = useState(issue.cost || 0);
  const [recharge, setRecharge] = useState(!!issue.recharge_to_landlord);
  const photos = issue.photos && issue.photos.length ? issue.photos : (issue.photo ? [issue.photo] : []);
  const afterPhotos = issue.resolution_photos || [];

  const readMany = (files) => Promise.all([...files].map((f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); })));
  const onAfter = async (e) => {
    const arr = await readMany(e.target.files);
    try { await api.patch(`/issues/${issue.id}`, { resolution_photos: [...afterPhotos, ...arr] }); toast.success("After-fix photo(s) added"); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const saveCost = async () => {
    try { await api.patch(`/issues/${issue.id}/cost`, { cost: Number(cost), recharge_to_landlord: recharge }); toast.success("Repair cost saved"); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="mt-2 space-y-2">
      {photos.length > 0 && (
        <div className="flex gap-2 flex-wrap" data-testid={`issue-photos-${issue.id}`}>
          {photos.map((src, i) => <img key={i} src={fileUrl(src)} alt="issue" className="h-20 w-20 rounded-md border border-border object-cover" />)}
        </div>
      )}
      {afterPhotos.length > 0 && (
        <div>
          <p className="text-[0.6rem] overline text-[#2B4C3F]">After-fix</p>
          <div className="flex gap-2 flex-wrap">{afterPhotos.map((src, i) => <img key={i} src={fileUrl(src)} alt="after" className="h-20 w-20 rounded-md border border-[#2B4C3F]/30 object-cover" />)}</div>
        </div>
      )}
      {staff && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">£</span>
            <Input data-testid={`issue-cost-${issue.id}`} type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="h-8 w-24 text-xs" placeholder="Cost" />
          </div>
          <label className="flex items-center gap-1.5 text-xs"><Switch data-testid={`issue-recharge-${issue.id}`} checked={recharge} onCheckedChange={setRecharge} />Recharge landlord</label>
          <Button size="sm" variant="outline" data-testid={`issue-save-cost-${issue.id}`} onClick={saveCost}>Save cost</Button>
          <Button size="sm" variant="ghost" data-testid={`issue-invoice-${issue.id}`} onClick={() => openPdf(`/issues/${issue.id}/invoice.pdf`, true)}><FileDown className="h-3.5 w-3.5 mr-1" />Invoice</Button>
          <label className="text-xs underline cursor-pointer" data-testid={`issue-afterfix-label-${issue.id}`}>
            + After-fix photo<input type="file" accept="image/*" multiple className="hidden" data-testid={`issue-afterfix-${issue.id}`} onChange={onAfter} />
          </label>
        </div>
      )}
    </div>
  );
}

const RENT_CLS = { paid: "bg-[#2B4C3F]/12 text-[#2B4C3F]", overdue: "bg-[#9E382A]/12 text-[#9E382A]", due: "bg-secondary text-muted-foreground" };

function IncomeDialog({ pid, onDone }) {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ amount: "", date: today, reference: "" });
  const submit = async () => {
    try {
      await api.post("/rent/entry", { property_id: pid, amount: Number(f.amount), date: f.date, reference: f.reference, paid: true });
      toast.success("Income recorded");
      setOpen(false); setF({ amount: "", date: today, reference: "" }); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="add-income-button"><Plus className="h-3.5 w-3.5 mr-1" />Record income</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-head">Record booking income</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline">Amount (£)</Label><Input data-testid="income-amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Date</Label><Input data-testid="income-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Reference / guest</Label><Input data-testid="income-ref" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button data-testid="save-income-button" onClick={submit} disabled={!f.amount}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FinanceTab({ property, role }) {
  const staff = ["admin", "operations"].includes(role);
  const pid = property.id;
  const t = terms(property.rental_type);
  const [rent, setRent] = useState([]);
  const [statements, setStatements] = useState([]);
  const [deposits, setDeposits] = useState([]);

  const load = () => {
    api.get(`/properties/${pid}/rent`).then((r) => setRent(r.data)).catch(() => {});
    api.get(`/properties/${pid}/statements`).then((r) => setStatements(r.data)).catch(() => {});
    api.get(`/properties/${pid}/deposits`).then((r) => setDeposits(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [pid]);

  const markPaid = async (r) => {
    try { await api.patch(`/rent/${r.id}`, { status: "paid", method: "Bank transfer" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      {/* Rent */}
      <div className="bg-white border border-border rounded-md">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <span className="overline flex items-center gap-1.5"><PoundSterling className="h-3.5 w-3.5" />{t.income} Ledger</span>
          {staff && (property.rental_type === "holiday"
            ? <IncomeDialog pid={pid} onDone={load} />
            : <RentScheduleDialog pid={pid} defaultAmount={property.monthly_rent} onDone={load} />)}
        </div>
        {rent.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No {t.income.toLowerCase()} recorded yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="px-5 py-2 overline">Due</th><th className="px-5 py-2 overline">Reference</th><th className="px-5 py-2 overline">Amount</th><th className="px-5 py-2 overline">Status</th><th className="px-5 py-2"></th></tr></thead>
            <tbody className="divide-y divide-border">
              {rent.map((r) => (
                <tr key={r.id} data-testid={`rent-${r.id}`}>
                  <td className="px-5 py-2">{fmtDate(r.due_date)}</td>
                  <td className="px-5 py-2 text-muted-foreground">{r.reference || (r.method === "booking" ? "Booking" : "Rent")}</td>
                  <td className="px-5 py-2 font-mono">{gbp(r.amount)}</td>
                  <td className="px-5 py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${RENT_CLS[r.status]}`}>{r.status}</span></td>
                  <td className="px-5 py-2 text-right">
                    {r.status === "paid"
                      ? <Button size="sm" variant="ghost" data-testid={`receipt-${r.id}`} onClick={() => openPdf(`/rent/${r.id}/receipt.pdf`, true)}><FileDown className="h-3.5 w-3.5 mr-1" />Receipt</Button>
                      : staff && <Button size="sm" variant="outline" data-testid={`mark-paid-${r.id}`} onClick={() => markPaid(r)}>Mark Paid</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Statements */}
      <div className="bg-white border border-border rounded-md">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <span className="overline">Owner Statements</span>
          {staff && <StatementDialog pid={pid} onDone={load} />}
        </div>
        {statements.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No statements generated.</p> : (
          <div className="divide-y divide-border">
            {statements.map((s) => (
              <div key={s.id} data-testid={`statement-${s.id}`} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium">{fmtDate(s.period_start)} – {fmtDate(s.period_end)}</p>
                  <p className="text-xs text-muted-foreground">{t.income} {gbp(s.rent_received)} · Comm {gbp(s.commission)} · Repairs {gbp(s.repair_recharges)}</p>
                </div>
                <p className="font-mono font-semibold text-[#2B4C3F]">{gbp(s.net_payout)}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === "paid" ? "bg-[#2B4C3F]/12 text-[#2B4C3F]" : "bg-secondary text-muted-foreground"}`}>{s.status}</span>
                <Button size="sm" variant="ghost" data-testid={`statement-pdf-${s.id}`} onClick={() => openPdf(`/statements/${s.id}/pdf`, true)}><FileDown className="h-3.5 w-3.5" /></Button>
                {staff && s.status !== "paid" && <Button size="sm" variant="outline" data-testid={`statement-pay-${s.id}`} onClick={async () => { await api.post(`/statements/${s.id}/pay`); toast.success("Marked paid to landlord"); load(); }}>Mark Paid</Button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deposits */}
      <div className="bg-white border border-border rounded-md">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <span className="overline flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Deposit</span>
          {staff && <DepositDialog pid={pid} onDone={load} />}
        </div>
        {deposits.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No deposit recorded.</p> : (
          <div className="divide-y divide-border">
            {deposits.map((d) => (
              <div key={d.id} data-testid={`deposit-${d.id}`} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1"><p className="text-sm font-medium">{gbp(d.amount)} · {d.scheme}</p><p className="text-xs text-muted-foreground">Ref {d.reference || "—"}{d.protected_date ? ` · Protected ${fmtDate(d.protected_date)}` : ""}</p></div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]">{d.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RentScheduleDialog({ pid, defaultAmount, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ monthly_amount: defaultAmount || 0, start_date: "", months: 12 });
  const submit = async () => {
    try { await api.post("/rent/schedule", { property_id: pid, monthly_amount: Number(f.monthly_amount), start_date: f.start_date, months: Number(f.months) }); toast.success("Rent schedule created"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="rent-schedule-button"><Plus className="h-4 w-4 mr-1" />Schedule Rent</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-head">Create Rent Schedule</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline">Monthly amount £</Label><Input data-testid="rent-amount-input" type="number" value={f.monthly_amount} onChange={(e) => setF({ ...f, monthly_amount: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Start date</Label><Input data-testid="rent-start-input" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Months</Label><Input type="number" value={f.months} onChange={(e) => setF({ ...f, months: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button data-testid="save-rent-schedule" onClick={submit} disabled={!f.start_date || !f.monthly_amount}>Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementDialog({ pid, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ period_start: "", period_end: "", extra_fees: 0, fee_note: "" });
  const submit = async () => {
    try { await api.post("/statements/generate", { property_id: pid, ...f, extra_fees: Number(f.extra_fees) }); toast.success("Statement generated"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="statement-generate-button"><Plus className="h-4 w-4 mr-1" />Generate</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-head">Generate Statement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline">Period start</Label><Input data-testid="stmt-start-input" type="date" value={f.period_start} onChange={(e) => setF({ ...f, period_start: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Period end</Label><Input data-testid="stmt-end-input" type="date" value={f.period_end} onChange={(e) => setF({ ...f, period_end: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Extra fees £</Label><Input type="number" value={f.extra_fees} onChange={(e) => setF({ ...f, extra_fees: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Fee note</Label><Input value={f.fee_note} onChange={(e) => setF({ ...f, fee_note: e.target.value })} className="mt-1.5" /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="save-statement" onClick={submit} disabled={!f.period_start || !f.period_end}>Generate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DepositDialog({ pid, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: 0, scheme: "DPS", reference: "", protected_date: "" });
  const submit = async () => {
    try { await api.post("/deposits", { property_id: pid, ...f, amount: Number(f.amount) }); toast.success("Deposit recorded"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="deposit-add-button"><Plus className="h-4 w-4 mr-1" />Add</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-head">Record Deposit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline">Amount £</Label><Input data-testid="deposit-amount-input" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5" /></div>
          <div>
            <Label className="overline">Scheme</Label>
            <Select value={f.scheme} onValueChange={(v) => setF({ ...f, scheme: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{["DPS", "TDS", "MyDeposits", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Reference</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Protected date</Label><Input type="date" value={f.protected_date} onChange={(e) => setF({ ...f, protected_date: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button data-testid="save-deposit" onClick={submit} disabled={!f.amount}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LegalTab({ property, role }) {
  const staff = ["admin", "operations", "compliance"].includes(role);
  const pid = property.id;
  const [inv, setInv] = useState([]);
  const [agr, setAgr] = useState([]);
  const load = () => {
    api.get(`/properties/${pid}/inventories`).then((r) => setInv(r.data)).catch(() => {});
    api.get(`/properties/${pid}/agreements`).then((r) => setAgr(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [pid]);

  return (
    <div className="space-y-6">
      <div className="bg-white border border-border rounded-md">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <span className="overline flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" />Inventory & Inspections</span>
          {staff && <InventoryDialog pid={pid} onDone={load} />}
        </div>
        {inv.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No inventory reports.</p> : (
          <div className="divide-y divide-border">
            {inv.map((i) => (
              <div key={i.id} data-testid={`inventory-${i.id}`} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1"><p className="text-sm font-medium">{i.title}</p><p className="text-xs text-muted-foreground">{(i.items || []).length} rooms · {fmtDate(i.created_at)} · {i.created_by}</p></div>
                <Button size="sm" variant="ghost" data-testid={`inventory-pdf-${i.id}`} onClick={() => openPdf(`/inventories/${i.id}/pdf`, true)}><FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-md">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <span className="overline flex items-center gap-1.5"><FileSignature className="h-3.5 w-3.5" />Tenancy Agreements</span>
          {["admin", "operations"].includes(role) && <AgreementDialog pid={pid} defaultRent={property.monthly_rent} onDone={load} />}
        </div>
        {agr.length === 0 ? <p className="p-8 text-center text-sm text-muted-foreground">No agreements.</p> : (
          <div className="divide-y divide-border">
            {agr.map((a) => (
              <div key={a.id} data-testid={`agreement-${a.id}`} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1"><p className="text-sm font-medium">{a.tenant_name}</p><p className="text-xs text-muted-foreground">{gbp(a.monthly_rent)}/mo · {a.term_months}mo from {fmtDate(a.start_date)}</p></div>
                <Button size="sm" variant="ghost" data-testid={`agreement-pdf-${a.id}`} onClick={() => openPdf(`/agreements/${a.id}/pdf`, true)}><FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryDialog({ pid, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("Inventory & Condition Report");
  const [items, setItems] = useState([{ room: "Living Room", condition: "Good", notes: "" }]);
  const upd = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const submit = async () => {
    try { await api.post("/inventories", { property_id: pid, title, items }); toast.success("Inventory saved"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="inventory-add-button"><Plus className="h-4 w-4 mr-1" />New Report</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">Inventory Report</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto thin-scroll pr-1">
          <div><Label className="overline">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" /></div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 items-end border-b border-border pb-2">
              <div><Label className="overline">Room</Label><Input value={it.room} onChange={(e) => upd(i, "room", e.target.value)} className="mt-1.5" /></div>
              <div>
                <Label className="overline">Condition</Label>
                <Select value={it.condition} onValueChange={(v) => upd(i, "condition", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Excellent", "Good", "Fair", "Poor"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="overline">Notes</Label><Input value={it.notes} onChange={(e) => upd(i, "notes", e.target.value)} className="mt-1.5" /></div>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setItems([...items, { room: "", condition: "Good", notes: "" }])}><Plus className="h-4 w-4 mr-1" />Add Room</Button>
        </div>
        <DialogFooter><Button data-testid="save-inventory" onClick={submit}>Save Report</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AgreementDialog({ pid, defaultRent, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ tenant_name: "", monthly_rent: defaultRent || 0, deposit: 0, start_date: "", term_months: 12, notes: "" });
  const submit = async () => {
    try { await api.post("/agreements", { property_id: pid, ...f, monthly_rent: Number(f.monthly_rent), deposit: Number(f.deposit), term_months: Number(f.term_months) }); toast.success("Agreement created"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="agreement-add-button"><Plus className="h-4 w-4 mr-1" />New Agreement</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-head">Tenancy Agreement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline">Tenant name</Label><Input data-testid="agr-tenant-input" value={f.tenant_name} onChange={(e) => setF({ ...f, tenant_name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Monthly rent £</Label><Input type="number" value={f.monthly_rent} onChange={(e) => setF({ ...f, monthly_rent: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Deposit £</Label><Input type="number" value={f.deposit} onChange={(e) => setF({ ...f, deposit: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Start date</Label><Input data-testid="agr-start-input" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Term (months)</Label><Input type="number" value={f.term_months} onChange={(e) => setF({ ...f, term_months: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label className="overline">Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button data-testid="save-agreement" onClick={submit} disabled={!f.tenant_name || !f.start_date}>Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
